/**
 * The campaign editor's "call the next one now" button (ring R4): the request
 * it sends and the one note it shows about what happened.
 *
 * A HAND-WRITTEN MIRROR of the call-next route's answer
 * (`apps/api/src/modules/crm-calls/outbound-auto.routes.ts`) and of the part of
 * the automation's tick report (`lib/outbound/auto/runner.ts`) the note reads.
 * The reason wording belongs to the automation tab (`aiCalls.automation.*`);
 * this file only picks the key, so a reason newer than the catalogue is said
 * with its code by the card instead of rendering as a raw key.
 *
 * Split out of `aiCampaignAutomation.ts`, which keeps the campaign's automation
 * fields and re-exports these names: the automation tab imports the request
 * and its reply type from there. React-free, so the answer-to-note mapping runs
 * under plain node.
 */
import { ApiError, api } from "./api";

const BASE = "/admin/crm/calls/ai-outbound/campaigns";

/** The part of the automation's tick report the button reads (`TickReport`, `runner.ts`). */
export interface CallNextReport {
  /** "placed", "refused", "decided" or "failed". */
  outcome: string;
  /** An automation reason, a placement refusal, "no_audience" or "tick_failed". */
  reason: string;
  test: boolean;
  companyName: string | null;
  /** Why the pressed campaign could not be worked, when that was the answer. */
  campaignSkip: string | null;
}

export interface CallNextReply {
  report: CallNextReport;
}

/**
 * "Call the next one now" for one campaign: a placed call answers 201 with the
 * report; anything else throws an `ApiError` whose body is `{ error, report }`
 * (or only `{ error }` when no tick ran: `tick_running`, `not_found`).
 */
export function callNextAiCampaign(id: string): Promise<CallNextReply> {
  return api.post<CallNextReply>(`${BASE}/${encodeURIComponent(id)}/call-next`);
}

/** Refusals that come without a report, each worded under `aiCalls.campaigns.automation.callNext.error`. */
export const CALL_NEXT_ERRORS = ["tick_running", "not_found", "forbidden", "failed"] as const;

export type CallNextNote =
  | { kind: "placed"; tone: "ok"; test: boolean; companyName: string | null }
  | {
      kind: "reason";
      tone: "warn" | "bad";
      /** A full catalogue key (the automation tab's wording); the card falls back when it is missing. */
      key: string;
      /** The server's code, printed by the fallback. */
      code: string;
      test: boolean;
    }
  | { kind: "error"; tone: "warn" | "bad"; code: (typeof CALL_NEXT_ERRORS)[number] };

/** Reasons a refused tick names that are not a placement refusal. */
const TICK_REASONS: ReadonlySet<string> = new Set(["no_audience", "tick_failed", "lease_held"]);

function readReport(raw: unknown): CallNextReport | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.outcome !== "string" || typeof r.reason !== "string") return null;
  return {
    outcome: r.outcome,
    reason: r.reason,
    test: r.test === true,
    companyName: typeof r.companyName === "string" && r.companyName.trim() ? r.companyName : null,
    campaignSkip: typeof r.campaignSkip === "string" && r.campaignSkip ? r.campaignSkip : null,
  };
}

/**
 * The catalogue key a report that placed nothing is worded with. The pressed
 * campaign's own skip wins (it says why THIS campaign could not be worked);
 * a refused placement is a placement reason; everything else, a decided or
 * failed tick included, is an automation reason.
 */
export function callNextReasonKey(report: Pick<CallNextReport, "outcome" | "reason" | "campaignSkip">): string {
  if (report.campaignSkip) return `aiCalls.automation.campaignSkip.${report.campaignSkip}`;
  if (report.outcome === "refused" && !TICK_REASONS.has(report.reason)) return `aiCalls.automation.placeReason.${report.reason}`;
  return `aiCalls.automation.reason.${report.reason}`;
}

function noteFromReport(report: CallNextReport): CallNextNote {
  if (report.outcome === "placed") return { kind: "placed", tone: "ok", test: report.test, companyName: report.companyName };
  return {
    kind: "reason",
    tone: report.outcome === "failed" ? "bad" : "warn",
    key: callNextReasonKey(report),
    code: report.campaignSkip ?? report.reason,
    test: report.test,
  };
}

/** What the button's note says, from the answer or from the thrown error. */
export function callNextNote(outcome: { reply: CallNextReply } | { error: unknown }): CallNextNote {
  if ("reply" in outcome) {
    const report = readReport(outcome.reply?.report);
    return report ? noteFromReport(report) : { kind: "error", tone: "bad", code: "failed" };
  }
  const err = outcome.error;
  if (!(err instanceof ApiError)) return { kind: "error", tone: "bad", code: "failed" };
  const body = (err.body ?? {}) as { error?: unknown; report?: unknown };
  const report = readReport(body.report);
  if (report) return noteFromReport(report);
  if (err.status === 403) return { kind: "error", tone: "bad", code: "forbidden" };
  if (body.error === "tick_running") return { kind: "error", tone: "warn", code: "tick_running" };
  if (err.status === 404 || body.error === "not_found") return { kind: "error", tone: "bad", code: "not_found" };
  return { kind: "error", tone: "bad", code: "failed" };
}
